// useGuide.ts — thin bridge from progress state to the pure guide selector.
// pick(hook, extra) returns a selection or null (honouring suppression + caps);
// record(sel) commits the impression. Suppression sources: programme paused, and
// the "I'm doing these my own way" opt-out (reflectionOwnWay).
import { useProgress } from '@/hooks/useProgress';
import { dayKey } from '@/services/dates';
import { selectGuideMessage, type SelectCtx, type GuideSelection } from '@/services/guidePresence';
import type { GuideHook, ActId } from '@/data/guideMessages';

export function useGuide() {
  const progress = useProgress();
  const s = progress.state;

  const pick = (hook: GuideHook, extra?: Partial<SelectCtx>): GuideSelection | null =>
    selectGuideMessage(hook, {
      paused: !!s.paused,
      myOwnWay: s.reflectionOwnWay,
      persist: {
        recentQuoteIds: s.guide.recentQuoteIds,
        seen: s.guide.seen,
        boundariesShown: s.guide.boundariesShown,
      },
      todayKey: dayKey(),
      ...extra,
    });

  const record = (sel: GuideSelection, act?: ActId) =>
    progress.recordGuideImpression({ hook: sel.hook, clipId: sel.clipId, quoteId: sel.quote?.id, act });

  return { pick, record, lastReturnHandled: s.guide.lastReturnHandled };
}
